<div [formGroup]="form">
  <div class="form-spacing">
    <!-- Data dichiarazione (View only) -->
    <div class="column">
      <label class="label">{{ 'pre-employment.selfservice.declaration-date.label' | translate }}</label>
      <p class="text-dark">{{ form.get('createdAt')?.value | date: 'dd/MM/yyyy' }}</p>
    </div>

    <!-- RapportiLavoro: Hai avuto precedenti rapporti di lavoro nell'anno corrente? -->
    <div class="column">
      <label for="kind" class="label">
        {{ 'selfservice-request.employment-history.had-previous-employment.label' | translate: { year: hiredAtYear } }}
        <span class="mr-1 text-red-500">*</span>
      </label>
      <div class="input-with-confirmation">
        <p-select
          id="kind"
          appendTo="body"
          formControlName="kind"
          optionValue="value"
          optionLabel="label"
          [options]="hadPreviousEmploymentOptions"
          placeholder="{{ 'selfservice-request.previous-work-activities.yes-no.placeholder' | translate }}"
          class="full-width"
          (onChange)="onChangeKind()"
        ></p-select>
        <button
          pButton
          type="button"
          class="confirm-field-button"
          [label]="'selfservice.dialog.confirm' | translate"
          (click)="confirmField('hasPreviousEmployment')"
          [disabled]="
            form.disabled || isSaving || isFieldConfirmed('hasPreviousEmployment') || form.get('kind')?.invalid
          "
          [loading]="isFieldSaving('hasPreviousEmployment')"
        ></button>
      </div>
      @if (form.get('kind')?.invalid && form.get('kind')?.touched) {
        <small class="text-red-500 p-error">
          {{ 'selfservice-request.employment-history.validation.required' | translate }}
        </small>
      }
    </div>

    @if (hasPreviousEmployment && isFieldConfirmed('hasPreviousEmployment')) {
      <ng-container>
        <!-- Notes -->
        <div class="column">
          <p>
            {{ 'selfservice-request.employment-history.previous-employment.notes' | translate }}
          </p>
        </div>
        <!-- Ha percepito/percepirà dal precedente datore di lavoro un trattamento di fine rapporto -->
        <div class="column">
          <label for="hasSeverancePay" class="label">
            {{ 'selfservice-request.employment-history.has-severance-pay.label' | translate: { year: hiredAtYear } }}
            <span class="mr-1 text-red-500">*</span>
          </label>
          <div class="input-with-confirmation">
            <p-select
              id="hasSeverancePay"
              appendTo="body"
              formControlName="hasSeverancePay"
              optionValue="value"
              optionLabel="label"
              [options]="yesNoOptions"
              placeholder="{{ 'selfservice-request.previous-work-activities.yes-no.placeholder' | translate }}"
              class="full-width"
            ></p-select>
            <button
              pButton
              type="button"
              class="confirm-field-button"
              [label]="'selfservice.dialog.confirm' | translate"
              (click)="confirmField('hasSeverancePay')"
              [disabled]="
                form.disabled || isSaving || isFieldConfirmed('hasSeverancePay') || form.get('hasSeverancePay')?.invalid
              "
              [loading]="isFieldSaving('hasSeverancePay')"
            ></button>
          </div>
          @if (form.get('hasSeverancePay')?.invalid && form.get('hasSeverancePay')?.touched) {
            <small class="text-red-500 p-error">
              {{ 'selfservice-request.employment-history.validation.required' | translate }}
            </small>
          }
        </div>

        <!-- Numero giorni malattia (nascosto in caso di Contratto Indeterminato) -->
        @if (showSickDays) {
          <div class="column">
            <label for="sickDaysCount" class="label">
              {{
                'selfservice-request.employment-history.sick-days-count.label'
                  | translate: { year: hiredAtYear, company: userCompany }
              }}
              <span class="mr-1 text-red-500">*</span>
              <span class="cursor-pointer italic" [pTooltip]="sickDaysTooltipContent" tooltipPosition="right">
                <i class="pi pi-question-circle"></i>
              </span>
              <ng-template #sickDaysTooltipContent>
                <span
                  [innerHTML]="
                    'selfservice-request.employment-history.sick-days-count.help' | translate: { year: hiredAtYear }
                  "
                ></span>
              </ng-template>
            </label>
            <div class="input-with-confirmation">
              <p-inputNumber
                inputId="sickDaysCount"
                formControlName="sickDaysCount"
                [step]="1"
                [useGrouping]="false"
                class="full-width"
                placeholder="{{ 'selfservice-request.employment-history.input.placeholder' | translate }}"
              ></p-inputNumber>
              <button
                pButton
                type="button"
                class="confirm-field-button"
                [label]="'selfservice.dialog.confirm' | translate"
                (click)="confirmField('sickDaysCount')"
                [disabled]="
                  form.disabled || isSaving || isFieldConfirmed('sickDaysCount') || form.get('sickDaysCount')?.invalid
                "
                [loading]="isFieldSaving('sickDaysCount')"
              ></button>
            </div>
            @if (form.get('sickDaysCount')?.invalid && form.get('sickDaysCount')?.touched) {
              <small class="text-red-500 p-error">
                {{
                  'selfservice-request.employment-history.sick-days-count.validation' | translate: { max: maxSickDays }
                }}
              </small>
            }
          </div>
        }
        @if (isFieldConfirmed('sickDaysCount')) {
          <p class="mb-5">
            <strong class="text-red-500">{{ 'selfservice-requests.complete.label' | translate }}</strong>
          </p>
        }

        <!-- Importo imponibile previdenziale (altro datore di lavoro) euro -->
        <div class="column">
          <div>
            <label for="taxableSocialSecurityAmount" class="label">
              {{
                'selfservice-request.employment-history.taxable-social-security-amount.label'
                  | translate: { year: hiredAtYear }
              }}
              <span class="mr-1 text-red-500">*</span>
              <span
                class="cursor-pointer italic"
                [pTooltip]="taxableSocialSecurityAmountTooltipContent"
                tooltipPosition="right"
              >
                <i class="pi pi-question-circle"></i>
              </span>
              <ng-template #taxableSocialSecurityAmountTooltipContent>
                <span
                  [innerHTML]="
                    'selfservice-request.employment-history.taxable-social-security-amount.help'
                      | translate: { year: hiredAtYear }
                  "
                ></span>
              </ng-template>
            </label>
            <p-button
              type="button"
              class="p-ml-4 p-pt-0 p-pb-0 ml-2"
              outlined="true"
              label="{{ 'selfService.open.file.label' | translate }}"
              (onClick)="openHelper('taxableSocialSecurityAmount')"
            ></p-button>
          </div>
          <div class="input-with-confirmation">
            <p-inputNumber
              inputId="taxableSocialSecurityAmount"
              formControlName="taxableSocialSecurityAmount"
              mode="currency"
              currency="EUR"
              locale="it-IT"
              [min]="0"
              [minFractionDigits]="0"
              [maxFractionDigits]="2"
              class="full-width"
              placeholder="{{ 'selfservice-request.employment-history.input.placeholder' | translate }}"
            ></p-inputNumber>
            <button
              pButton
              type="button"
              class="confirm-field-button"
              [label]="'selfservice.dialog.confirm' | translate"
              (click)="confirmField('taxableSocialSecurityAmount')"
              [disabled]="
                form.disabled ||
                isSaving ||
                isFieldConfirmed('taxableSocialSecurityAmount') ||
                form.get('taxableSocialSecurityAmount')?.invalid
              "
              [loading]="isFieldSaving('taxableSocialSecurityAmount')"
            ></button>
          </div>
          @if (form.get('taxableSocialSecurityAmount')?.invalid && form.get('taxableSocialSecurityAmount')?.touched) {
            <small class="text-red-500 p-error">
              {{ 'selfservice-request.employment-history.validation.required' | translate }}
            </small>
          }
        </div>

        <!-- Importo compensi in natura/fringe benefit lordi (altro datore di lavoro) euro -->
        <div class="column">
          <div>
            <label for="fringeBenefitsAmount" class="label">
              {{
                'selfservice-request.employment-history.fringe-benefits-amount.label' | translate: { year: hiredAtYear }
              }}
              <span class="mr-1 text-red-500">*</span>
              <span
                class="cursor-pointer italic"
                [pTooltip]="fringeBenefitsAmountTooltipContent"
                tooltipPosition="right"
              >
                <i class="pi pi-question-circle"></i>
              </span>
              <ng-template #fringeBenefitsAmountTooltipContent>
                <span
                  [innerHTML]="
                    'selfservice-request.employment-history.fringe-benefits-amount.help'
                      | translate: { year: hiredAtYear }
                  "
                ></span>
              </ng-template>
            </label>
            <p-button
              type="button"
              class="p-ml-4 p-pt-0 p-pb-0 ml-2"
              outlined="true"
              label="{{ 'selfService.open.file.label' | translate }}"
              (onClick)="openHelper('fringeBenefitsAmount')"
            ></p-button>
          </div>
          <div class="input-with-confirmation">
            <p-inputNumber
              inputId="fringeBenefitsAmount"
              formControlName="fringeBenefitsAmount"
              mode="currency"
              currency="EUR"
              locale="it-IT"
              [min]="0"
              [minFractionDigits]="0"
              [maxFractionDigits]="2"
              class="full-width"
              placeholder="{{ 'selfservice-request.employment-history.input.placeholder' | translate }}"
            ></p-inputNumber>
            <button
              pButton
              type="button"
              class="confirm-field-button"
              [label]="'selfservice.dialog.confirm' | translate"
              (click)="confirmField('fringeBenefitsAmount')"
              [disabled]="
                form.disabled ||
                isSaving ||
                isFieldConfirmed('fringeBenefitsAmount') ||
                form.get('fringeBenefitsAmount')?.invalid
              "
              [loading]="isFieldSaving('fringeBenefitsAmount')"
            ></button>
          </div>
          @if (form.get('fringeBenefitsAmount')?.invalid && form.get('fringeBenefitsAmount')?.touched) {
            <small class="text-red-500 p-error">
              {{ 'selfservice-request.employment-history.validation.required' | translate }}
            </small>
          }
        </div>
      </ng-container>
    }
  </div>
</div>

<app-repository-show-file-dialog
  [visible]="showHelperDialog()"
  [fileType]="detailFileType()"
  (close)="onCloseShowHelperDialog()"
  [fileUrl]="getHelperImage()"
  [fileName]="fileName() || ''"
/>
